// Dependencies
import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowLeftIcon, ArrowRightIcon } from '../../../assets/icons';

// Components
import { Dialog } from '../../Dialog';

// Styles
import styles from './styles';

interface IAutomationConfigDialogProps {
  open: boolean;
  template: any;
  isAutomated?: boolean;
  automationFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  onClose: () => void;
  onSave: (config: { isAutomated: boolean; automationFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly' }) => void;
}

// Export automation config dialog
export const AutomationConfigDialog: FC<IAutomationConfigDialogProps> = ({ 
  open, 
  template, 
  automationFrequency: initialFrequency = 'daily',
  onClose, 
  onSave 
}) => {
  // States
  const [automationFrequency, setAutomationFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>(initialFrequency);

  // Update state when props change
  useEffect(() => {
    setAutomationFrequency(initialFrequency);
  }, [initialFrequency]);

  // Handle frequency change
  const handleFrequencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutomationFrequency(event.target.value as 'daily' | 'weekly' | 'monthly' | 'yearly');
  };

  // Handle save - document is already in automation mode, only frequency can be changed
  const handleSave = () => {
    onSave({
      isAutomated: true, // Always true since document is already automated
      automationFrequency
    });
  };

  if (!template) return null;

  // Return automation config dialog
  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <Typography mb={35} variant="title">
        Configure Automation
      </Typography>

      <Box display="flex" justifyContent="center" gap={16}>
        {/* Left side - Template card */}
        <Box sx={styles.templateCard}>
          <Box sx={styles.templateImage}>
            <img src={template.thumbnail_url} alt={template.name} />
          </Box>
        </Box>

        {/* Right side - Configuration */}
        <Box sx={styles.configSection}>
          <Typography mb={24} variant="title">
            Frequency
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup value={automationFrequency} onChange={handleFrequencyChange}>
              <FormControlLabel value="daily" control={<Radio />} label="Daily" />
              <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
              <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
              <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>

      <Stack direction="row" justifyContent="space-between" mt={40}>
        <Button variant="varied-outlined" startIcon={<ArrowLeftIcon />} onClick={onClose}>
          Back
        </Button>
        <Button variant="varied-outlined" endIcon={<ArrowRightIcon />} onClick={handleSave}>
          Save
        </Button>
      </Stack>
    </Dialog>
  );
};
